import { IndividualDiseaseRisk, RiskScoreResponse } from '@/api/generated-catch-backend/model';
import { ROUTE_RISK_ANALYSIS, ROUTE_SINGLE_CANCER_VIEW } from '@/constants/routes';
import useAnalyticsCalls, { DashboardSource } from '@/hooks/use-analytics';
import { getRoute } from '@/utils';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DropdownSelect from './components/dropdown-select';
import { TopRisksCard } from './components/top-risks-card';

interface Top3RisksProps {
  risks?: RiskScoreResponse;
}

export default function Top3Risks({ risks }: Top3RisksProps) {
  const [activeTab, setActiveTab] = useState<'relative' | 'absolute'>('relative');
  const navigate = useNavigate();
  const { callToggleRisk, setNavigationSource } = useAnalyticsCalls();

  const handleTabChange = (newTab: 'relative' | 'absolute') => {
    const previousTab = activeTab ?? 'relative';
    callToggleRisk(previousTab, newTab);
    setActiveTab(newTab);
  };

  const dropdownOptions = [
    { value: 'relative', label: 'Relative risk' },
    { value: 'absolute', label: 'Absolute risk' },
  ];

  const tabConfig = useMemo(
    () => ({
      relative: {
        label: 'Relative Risk',
        risks: risks?.topDiseasesByRelativeRisk,
        subtitle: `These are the cancers where your personal risk is most elevated compared to the average person.`,
        getRiskValue: (risk: IndividualDiseaseRisk) => risk.lifetimeRisk,
      },
      absolute: {
        label: 'Absolute Risk',
        risks: risks?.topDiseasesByAbsoluteRisk,
        subtitle: `These are the cancers you're most likely to develop over your lifetime.`,
        getRiskValue: (risk: IndividualDiseaseRisk) => risk.lifetimeRisk,
      },
    }),
    [risks],
  );

  const currentConfig = tabConfig[activeTab ?? 'relative'];

  if (!risks) {
    return (
      <div className="risk-cards-container">
        {/* Mobile dropdown skeleton */}
        <div className="mb-4 md:hidden">
          <div className="h-9 w-[183px] animate-pulse rounded bg-gray-200" />
        </div>

        {/* Desktop layout skeleton */}
        <div className="hidden w-full max-w-[650px] md:flex md:items-start md:justify-between">
          <div className="flex-1">
            <div className="h-7 w-48 animate-pulse rounded-full bg-gray-300" />
            <div className="mt-2 h-5 w-full animate-pulse rounded-full bg-gray-200" />
          </div>
          <div className="ml-4 flex-shrink-0">
            <div className="h-9 w-[183px] animate-pulse rounded bg-gray-200" />
          </div>
        </div>

        {/* Mobile title/subtitle skeleton */}
        <div className="w-full max-w-[650px] md:hidden">
          <div className="h-7 w-48 animate-pulse rounded-full bg-gray-300" />
          <div className="mt-2 h-5 w-full animate-pulse rounded-full bg-gray-200" />
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:justify-center md:max-w-[750px] md:mx-auto">
          {[1, 2, 3].map((index) => (
            <div key={`skeleton-${index}`} className="risk-card">
              <div className="risk-card-content">
                <div className="risk-card-info">
                  {/* Desktop badge skeleton */}
                  <div className="risk-badge-container hidden pb-2 md:flex">
                    <div className="h-[21px] w-[45px] animate-pulse rounded bg-gray-200" />
                  </div>

                  <div className="risk-title-row">
                    <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                    {/* Mobile badge skeleton */}
                    <div className="risk-badge-container md:hidden">
                      <div className="h-[21px] w-[45px] animate-pulse rounded bg-gray-200" />
                    </div>
                  </div>
                  <div className="risk-progress-row">
                    <div className="space-y-1">
                      <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex h-[39px] w-[39px] items-center justify-center rounded-full bg-[#f0f5f6] p-3 md:-mt-2 md:self-start">
                <div className="h-4 w-4 animate-pulse rounded-full bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
        <div className="cta-button-container">
          <div className="h-10 w-[293px] animate-pulse rounded-full bg-gray-300" />
        </div>
      </div>
    );
  }

  const onDiseaseClick = (disease: string) => {
    setNavigationSource(DashboardSource.DASHBOARD_SPECIFIC_CANCER);
    navigate(getRoute(ROUTE_SINGLE_CANCER_VIEW, { cancerId: disease }));
  };

  return (
    <div className="risk-cards-container">
      {/* Mobile dropdown - appears above title */}
      <div className="md:hidden">
        <DropdownSelect
          options={dropdownOptions}
          value={activeTab}
          onChange={(value) => handleTabChange(value as 'relative' | 'absolute')}
          className="max-w-[200px]"
        />
      </div>

      {/* Desktop layout - title/subtitle and dropdown side by side */}
      <div className="hidden w-full max-w-[650px] md:flex md:items-start md:justify-between">
        <div className="flex-1">
          <h2 className="mobile-h3-primary text-primary text-left font-bold">
            Your Top 3 Cancer Risks
          </h2>
          {/* Subtitle */}
          <AnimatePresence mode="wait">
            <motion.p
              key={activeTab}
              className="body-regular text-primary pt-2 text-left"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              {currentConfig.subtitle}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Desktop dropdown */}
        <div className="ml-4 flex-shrink-0">
          <DropdownSelect
            options={dropdownOptions}
            value={activeTab}
            onChange={(value) => handleTabChange(value as 'relative' | 'absolute')}
            className="w-[183px]"
          />
        </div>
      </div>

      {/* Mobile layout */}
      <div className="w-full max-w-[650px] md:hidden">
        <h2 className="mobile-h3-primary text-primary text-left font-bold">
          Your Top 3 Cancer Risks
        </h2>
        <AnimatePresence mode="wait">
          <motion.p
            key={activeTab}
            className="body-regular text-primary text-left pt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            {currentConfig.subtitle}
          </motion.p>
        </AnimatePresence>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          className="flex flex-col gap-3 md:flex-row md:justify-center md:max-w-[750px] md:mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {currentConfig.risks
            ?.slice(0, 3)
            .map((risk, index) => (
              <TopRisksCard
                key={`cancer-${activeTab}-${index}`}
                disease={risk.disease}
                diseaseSlug={risk.diseaseSlug}
                riskValue={currentConfig.getRiskValue(risk)}
                benchmark={risk.benchmark}
                populationAverage={risk.populationAverage}
                categoryFreeName={risk.categoryFreeName}
                variant="responsive"
                badgePosition="responsive"
                onClick={onDiseaseClick}
                showArrow={true}
                className="risk-card"
              />
            )) ?? []}
        </motion.div>
      </AnimatePresence>

      <div className="cta-button-container">
        <button
          onClick={() => {
            setNavigationSource(DashboardSource.DASHBOARD_CANCER_CTA);
            navigate(ROUTE_RISK_ANALYSIS);
          }}
          className="cta-button body-meta"
        >
          See All Cancers
        </button>
      </div>
    </div>
  );
}
